import { useCallback, useContext, useEffect, useRef, useState } from "react";

// Context
import SnackbarContext from "../../../context/psikotest/SnackbarContext";

// Handler
import errorHandler from "../../../helper/psikotest/errorHandler";

// Controller
import ParticipantSession from "../../../controller/psikotest/psikotest_participant/session";
import { CheckCircleIcon, ClockIcon } from "@heroicons/react/24/outline";

type MbtiAnswerItem = { answer: string; value: string }

function buildMbtiResult(c_answer: MbtiAnswerItem[]) {
    return {
        i: c_answer.filter(c => c.value === "(I) Introvert").length,
        e: c_answer.filter(c => c.value === "(E) Extrovert").length,
        s: c_answer.filter(c => c.value === "(S) Sensing").length,
        n: c_answer.filter(c => c.value === "(N) Intuition").length,
        t: c_answer.filter(c => c.value === "(T) Thinking").length,
        f: c_answer.filter(c => c.value === "(F) Feeling").length,
        j: c_answer.filter(c => c.value === "(J) Judging").length,
        p: c_answer.filter(c => c.value === "(P) Perceiving").length
    }
}

function SubtestMbti(props: { assessment_participant_session: TPPAssessmentParticipantSession, closeSession: () => void }) {
    // Context
    const { setNotif } = useContext(SnackbarContext);

    // state
    const [mbti_questionary, setMbtiQuestionary] = useState<TPSMbtiQuestionary[]>([])
    const [answer_questionary, setAnswerQuestionary] = useState<MbtiAnswerItem[]>([])
    const [pendingSaves, setPendingSaves] = useState(0)
    const [isFinishing, setIsFinishing] = useState(false)

    // Keep latest answers for concurrent clicks; serialize saves so late requests cannot overwrite newer state
    const answerRef = useRef<MbtiAnswerItem[]>([])
    const answerMetaRef = useRef<TPSAssessmentParticipantMbtiSessionAnswer | undefined>(undefined)
    const saveChainRef = useRef(Promise.resolve())
    const pendingSavesRef = useRef(0)

    const bumpPendingSaves = (delta: number) => {
        pendingSavesRef.current = Math.max(0, pendingSavesRef.current + delta)
        setPendingSaves(pendingSavesRef.current)
    }

    // get doc
    const getDoc = useCallback(async () => {
        try {
            // get all question in subtest id
            const psc = new ParticipantSession();
            let resQuestionaryMbti = await psc.getQuestionaryMbti();
            setMbtiQuestionary(resQuestionaryMbti.data.list)
            let resQuestionaryAnswerMbti = await psc.getQuestionaryAnswerMbti(props.assessment_participant_session.id)
            answerMetaRef.current = resQuestionaryAnswerMbti.data.row
            // set answer questionary
            const parsedAnswer: MbtiAnswerItem[] = JSON.parse(resQuestionaryAnswerMbti.data.row.answer)
            const nextAnswers = parsedAnswer.length === 0
                ? resQuestionaryMbti.data.list.map(() => ({ answer: "", value: "" }))
                : parsedAnswer
            answerRef.current = nextAnswers
            setAnswerQuestionary(nextAnswers)
        } catch (error) {
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        }
    }, [setNotif, props.assessment_participant_session.id]);

    useEffect(() => {
        getDoc();
    }, [getDoc]);

    // handleAnswer — optimistic UI, Finish waits until BE catch-up (pendingSaves === 0)
    const handleAnswer = (index: number, answer: string, point: string) => {
        // apply on latest ref immediately so rapid clicks never lose prior answers
        const c_answer = [...answerRef.current]
        c_answer.splice(index, 1, { answer, value: point })
        answerRef.current = c_answer
        setAnswerQuestionary(c_answer)

        if (!answerMetaRef.current) {
            return
        }

        const sessionId = props.assessment_participant_session.id
        const meta = answerMetaRef.current

        bumpPendingSaves(1)
        // queue saves; each flush persists the latest ref snapshot
        saveChainRef.current = saveChainRef.current
            .catch(() => undefined)
            .then(async () => {
                try {
                    const toSave = answerRef.current
                    const psc = new ParticipantSession();
                    await psc.saveQuestionaryAnswerMbti(sessionId, {
                        ...meta,
                        answer: JSON.stringify(toSave),
                        result: JSON.stringify(buildMbtiResult(toSave))
                    });
                } catch (error) {
                    const errorMessage = errorHandler(error);
                    setNotif({ type: "error", message: errorMessage });
                } finally {
                    bumpPendingSaves(-1)
                }
            })
    }

    const answeredCount = answer_questionary.filter(aq => aq.answer !== "").length
    const totalQuestions = mbti_questionary.length
    const allAnswered = totalQuestions > 0 && answeredCount === totalQuestions
    const isSaving = pendingSaves > 0
    const canFinish = allAnswered && !isSaving && !isFinishing

    // Ensure queued saves land before close (avoids close-then-reject leaving incomplete DB answers)
    const handleFinish = async () => {
        if (!canFinish) {
            return
        }
        setIsFinishing(true)
        try {
            await saveChainRef.current.catch(() => undefined)
            if (answerMetaRef.current) {
                const toSave = answerRef.current
                const psc = new ParticipantSession();
                await psc.saveQuestionaryAnswerMbti(props.assessment_participant_session.id, {
                    ...answerMetaRef.current,
                    answer: JSON.stringify(toSave),
                    result: JSON.stringify(buildMbtiResult(toSave))
                });
            }
            props.closeSession()
        } catch (error) {
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        } finally {
            setIsFinishing(false)
        }
    }

    return (
        <div className="h-[94%] overflow-auto">
            <div className="flex flex-col items-center">
                <div className="text-sm md:text-base max-w-5xl text-center py-3">
                    Di bawah ini ada 60 nomor. Masing-masing nomor memiliki dua pernyataan yang bertolak belakang <b>(PERNYATAAN A & B)</b>. <br />
                    Pilihlah salah satu pernyataan yang paling sesuai dengan diri anda dengan <b>mengklik</b> pada pilihan anda.<br />
                    Anda <b>HARUS</b> memilih salah satu yang dominan serta mengisi semua nomor.
                </div>
            </div>
            <div className="text-sm md:text-base border-b flex flex-col items-center pb-20">
                <div className="max-w-5xl">
                    <div className="">
                        <div className="flex w-full bg-slate-800 text-white font-semibold">
                            <div className="w-1/12 text-center flex justify-center items-center border p-1">No</div>
                            <div className={`w-5/12 text-center flex justify-center items-center border p-1 cursor-pointer`}>
                                Pernyataan A
                            </div>
                            <div className={`w-5/12 text-center flex justify-center items-center border p-1 cursor-pointer`}>
                                Pernyataan B
                            </div>
                            <div className="w-1/12 text-center flex justify-center items-center border p-1">
                                <CheckCircleIcon className="w-5 text-white" />
                            </div>
                        </div>
                    </div>
                    {mbti_questionary.map((mbti_q, index) => (
                        <div key={index} className="">
                            <div className="flex w-full">
                                <div className="w-1/12 text-center flex justify-center items-center border p-1">{mbti_q.number}</div>
                                <div
                                    className={`
                                        w-5/12 text-center flex justify-center items-center border p-1 cursor-pointer hover:bg-blue-200 hover:font-semibold hover:text-black
                                        ${answer_questionary?.[index]?.answer === "A" ? "bg-green-200" : ""}
                                        ${answer_questionary?.[index]?.answer !== "A" && answer_questionary?.[index]?.answer === "B" ? "bg-slate-100 text-gray-400" : ""}
                                    `}
                                    onClick={() => handleAnswer(index, "A", mbti_q.statement_a_value)}>
                                    {mbti_q.statement_a}
                                </div>
                                <div
                                    className={`
                                        w-5/12 text-center flex justify-center items-center border p-1 cursor-pointer hover:bg-blue-200 hover:font-semibold hover:text-black
                                        ${answer_questionary?.[index]?.answer === "B" ? "bg-green-200" : ""}
                                        ${answer_questionary?.[index]?.answer !== "B" && answer_questionary?.[index]?.answer === "A" ? "bg-slate-100 text-gray-400" : ""}
                                    `}
                                    onClick={() => handleAnswer(index, "B", mbti_q.statement_b_value)}>
                                    {mbti_q.statement_b}
                                </div>
                                <div className={`w-1/12 text-center flex justify-center items-center border p-1 ${answer_questionary?.[index]?.answer !== "" ? "bg-green-100" : ""}`}>
                                    {answer_questionary?.[index]?.answer !== ""
                                        ? <CheckCircleIcon className="w-5 text-green-600" />
                                        : <ClockIcon className="w-5 text-gray-400" />
                                    }
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="max-w-5xl w-full mt-3 flex justify-between items-center">
                    <div className="pl-2 md:pl-6 font-semibold">
                        {answeredCount}/{totalQuestions}
                        {isSaving && <span className="ml-2 font-normal text-amber-600">Menyimpan...</span>}
                    </div>
                    {isSaving
                        ? <div className="px-3 text-amber-600">Menunggu jawaban tersimpan sebelum <b>Finish</b></div>
                        : !allAnswered
                            ? <div className="px-3 text-red-500">Tombol <b>Finish</b> tersedia saat semua pernyataan telah dipilih</div>
                            : null
                    }
                    <button
                        type="button"
                        className={`mx-3 md:mx-0 py-1 px-6 min-w-[80px] text-center cursor-pointer rounded text-white bg-red-500 hover:bg-red-600 disabled:bg-gray-300`}
                        onClick={() => handleFinish()}
                        disabled={!canFinish}>
                        {isFinishing ? "..." : "Finish"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SubtestMbti;
